import { View, StyleSheet, ViewStyle } from 'react-native'
import Animated, { useSharedValue, useAnimatedStyle, withRepeat, withTiming, Easing, interpolate } from 'react-native-reanimated'
import { useEffect } from 'react'
import { LinearGradient } from 'expo-linear-gradient'
import { borderRadius, colors } from '../theme'

interface SkeletonProps {
  width?: number | string
  height?: number
  radius?: number
  style?: ViewStyle
}

function SkeletonBlock({ width = '100%', height = 16, radius = borderRadius.sm, style }: SkeletonProps) {
  const shimmer = useSharedValue(0)

  useEffect(() => {
    shimmer.value = withRepeat(
      withTiming(1, { duration: 1200, easing: Easing.inOut(Easing.ease) }),
      -1,
      false
    )
  }, [])

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: interpolate(shimmer.value, [0, 1], [-300, 300]) }],
  }))

  return (
    <View
      style={[
        {
          width: width as any,
          height,
          borderRadius: radius,
          backgroundColor: '#e2e8f0',
          overflow: 'hidden',
        },
        style,
      ]}
    >
      <Animated.View style={[StyleSheet.absoluteFillObject, animatedStyle]}>
        <LinearGradient
          colors={['transparent', 'rgba(255,255,255,0.5)', 'transparent']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={{ width: 300, height: '100%' }}
        />
      </Animated.View>
    </View>
  )
}

/** Card-shaped skeleton with typical dashboard layout */
export function SkeletonCard({ style }: { style?: ViewStyle }) {
  return (
    <View style={[styles.card, style]}>
      <SkeletonBlock width={80} height={12} />
      <SkeletonBlock width="60%" height={20} style={{ marginTop: 8 }} />
      <SkeletonBlock width="40%" height={12} style={{ marginTop: 8 }} />
    </View>
  )
}

/** Row-shaped skeleton for list items */
export function SkeletonRow({ style }: { style?: ViewStyle }) {
  return (
    <View style={[styles.row, style]}>
      <SkeletonBlock width={40} height={40} radius={20} />
      <View style={{ flex: 1, gap: 6 }}>
        <SkeletonBlock width="70%" height={14} />
        <SkeletonBlock width="90%" height={12} />
        <SkeletonBlock width="40%" height={12} />
      </View>
    </View>
  )
}

/** Full dashboard skeleton */
export function DashboardSkeleton() {
  return (
    <View style={styles.dashboard}>
      {/* Header */}
      <SkeletonBlock width={180} height={24} />
      <SkeletonBlock width={240} height={14} style={{ marginTop: 4 }} />
      {/* Hero card */}
      <SkeletonBlock width="100%" height={100} radius={borderRadius.lg} style={{ marginTop: 16 }} />
      {/* Stats row */}
      <View style={styles.statsRow}>
        <SkeletonBlock width="31%" height={64} radius={borderRadius.md} />
        <SkeletonBlock width="31%" height={64} radius={borderRadius.md} />
        <SkeletonBlock width="31%" height={64} radius={borderRadius.md} />
      </View>
      {/* Agreement cards */}
      <SkeletonBlock width={120} height={16} style={{ marginTop: 16 }} />
      <View style={styles.cardsRow}>
        <SkeletonBlock width="31%" height={120} radius={borderRadius.md} />
        <SkeletonBlock width="31%" height={120} radius={borderRadius.md} />
        <SkeletonBlock width="31%" height={120} radius={borderRadius.md} />
      </View>
    </View>
  )
}

/** Stats card skeleton — standalone stat block */
export function StatsSkeleton({ style }: { style?: ViewStyle }) {
  return (
    <View style={[styles.statsCard, style]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <SkeletonBlock width={16} height={16} radius={4} />
        <SkeletonBlock width={60} height={12} />
      </View>
      <SkeletonBlock width={72} height={24} />
    </View>
  )
}

/** Inbox list skeleton */
export function InboxSkeleton() {
  return (
    <View style={{ gap: 1 }}>
      {[0, 1, 2, 3, 4].map(i => <SkeletonRow key={i} />)}
    </View>
  )
}

/** Chat screen skeleton — message bubbles + input */
export function ChatSkeleton() {
  return (
    <View style={styles.chatContainer}>
      {/* Bot message */}
      <View style={styles.chatBotRow}>
        <SkeletonBlock width={32} height={32} radius={borderRadius.sm} />
        <SkeletonBlock width="66%" height={64} radius={borderRadius.lg} />
      </View>
      {/* User message */}
      <View style={styles.chatUserRow}>
        <SkeletonBlock width="50%" height={44} radius={borderRadius.lg} />
        <SkeletonBlock width={32} height={32} radius={borderRadius.sm} />
      </View>
      {/* Bot message */}
      <View style={styles.chatBotRow}>
        <SkeletonBlock width={32} height={32} radius={borderRadius.sm} />
        <SkeletonBlock width="75%" height={100} radius={borderRadius.lg} />
      </View>
      {/* Input area */}
      <View style={styles.chatInputRow}>
        <SkeletonBlock width="85%" height={44} radius={borderRadius.md} />
        <SkeletonBlock width={44} height={44} radius={borderRadius.md} />
      </View>
    </View>
  )
}

/** Market screen skeleton */
export function MarketSkeleton() {
  return (
    <View style={styles.dashboard}>
      <SkeletonBlock width={160} height={20} />
      <SkeletonBlock width={240} height={14} style={{ marginTop: 4 }} />
      {[0, 1, 2, 3, 4, 5].map(i => (
        <View key={i} style={[styles.row, { marginTop: 8 }]}>
          <SkeletonBlock width={40} height={40} radius={8} />
          <View style={{ flex: 1, gap: 4 }}>
            <SkeletonBlock width="50%" height={14} />
            <SkeletonBlock width="80%" height={12} />
          </View>
          <SkeletonBlock width={60} height={14} />
        </View>
      ))}
    </View>
  )
}

/** Regninger (bills) skeleton */
export function RegningerSkeleton() {
  return (
    <View style={styles.dashboard}>
      <SkeletonBlock width={140} height={20} />
      <View style={[styles.statsRow, { marginTop: 12 }]}>
        <SkeletonBlock width="48%" height={72} radius={borderRadius.md} />
        <SkeletonBlock width="48%" height={72} radius={borderRadius.md} />
      </View>
      {[0, 1, 2, 3].map(i => (
        <View key={i} style={[styles.row, { marginTop: 8 }]}>
          <SkeletonBlock width={36} height={36} radius={8} />
          <View style={{ flex: 1, gap: 4 }}>
            <SkeletonBlock width="60%" height={14} />
            <SkeletonBlock width="40%" height={12} />
          </View>
          <SkeletonBlock width={70} height={14} />
        </View>
      ))}
    </View>
  )
}

/** Single text line skeleton */
export function TextSkeleton({ width = '100%' }: { width?: number | string }) {
  return <SkeletonBlock width={width} height={14} />
}

/** Avatar circle skeleton */
export function AvatarSkeleton({ size = 40 }: { size?: number }) {
  return <SkeletonBlock width={size} height={size} radius={size / 2} />
}

/** Button-shaped skeleton */
export function ButtonSkeleton({ width = 100, height = 40 }: { width?: number; height?: number }) {
  return <SkeletonBlock width={width} height={height} radius={borderRadius.sm} />
}

export { SkeletonBlock }

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.background,
    borderRadius: borderRadius.md,
    padding: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: colors.background,
  },
  dashboard: {
    padding: 16,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 12,
  },
  cardsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  statsCard: {
    backgroundColor: colors.background,
    borderRadius: borderRadius.md,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.border,
  },
  chatContainer: {
    gap: 16,
    padding: 16,
  },
  chatBotRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  chatUserRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: 8,
  },
  chatInputRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 16,
  },
})
